// AmsApprovals.tsx — Arrears Team recommends, Property Manager approves, Finance & Admin Manager
// signs off a summons. The law firm is told only after the last approval.
//
// The Approve button is ABSENT on a blocked case, not disabled. A greyed-out Approve invites the
// question of who is allowed to un-grey it; absence answers it. The server refuses these anyway —
// this is the UI agreeing with the server rather than substituting for it.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type AmsApproval } from '../../lib/api'
import { dateOf, money, ROLE_LABELS } from '../../lib/format'
import DataTable from '../DataTable'

export default function AmsApprovals() {
  const { me, identity } = useAuth()
  const org = me?.org?.id
  const [rows, setRows] = useState<AmsApproval[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<Record<string, string>>({})

  const load = () => {
    if (!org) return
    api.ams.approvals(org).then(d => setRows(d.approvals))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }
  useEffect(load, [org])

  async function decide(a: AmsApproval, decision: 'approve' | 'return') {
    if (!org) return
    try {
      const r = await api.ams.decide(org, a.id, decision)
      setNote(n => ({ ...n, [a.id]: decision === 'approve'
        ? `Approved. TCMS ${r.send?.status}. Balance verified at ${money(r.balanceAtApproval ?? 0)}.`
        : 'Returned to the Arrears Team.' }))
      load()
    } catch (e) {
      // A refusal here is the product working. The reason is shown verbatim, because it is the
      // explanation an officer needs — a stale balance, a hardship rule, or the wrong role.
      setNote(n => ({ ...n, [a.id]: e instanceof ApiError ? `Refused: ${e.detail}` : String(e) }))
    }
  }

  const mayApprove = (a: AmsApproval) => {
    const r = identity.role
    if (!r) return false
    if (a.approverRole === 'finance_admin_manager') return r === 'finance_admin_manager' || r === 'tc_admin'
    return r === 'property_manager' || r === 'tc_admin'
  }

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!rows) return <div className="page-state">Loading…</div>

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Approvals</h2>
          <p className="muted">
            Before any approval the case is checked against the hardship rules, and the balance is
            re-read from TCMS. A resident who has paid since the recommendation cannot be sent a
            demand.
          </p>
        </div>
      </header>

      <section className="card">
        {/* Columns declared once; a table at a desk, stacked records on a phone. The approve /
            send-back control is `wide` so a two-button row is never squeezed into a third of a
            phone screen — a mis-tap here sends a demand letter. */}
        <DataTable
          rows={rows}
          rowKey={a => a.id}
          rowClass={a => (a.blocked ? 'row-blocked' : undefined)}
          empty="Nothing waiting for approval."
          columns={[
            { key: 'resident', header: 'Resident', role: 'primary', cell: a => a.residentName },
            { key: 'case', header: 'Case', role: 'secondary',
              cell: a => <Link to={`/ams/cases/${a.caseId}`}>{a.accountNo}</Link> },
            { key: 'stage', header: 'Letter', role: 'trailing',
              cell: a => <span className="tag stage">{a.stageName}</span> },
            { key: 'owed', header: 'Owed', num: true, cell: a => money(a.balanceCents) },
            { key: 'days', header: 'Days', num: true, cell: a => a.daysOverdue },
            { key: 'fee', header: 'Fee', num: true,
              cell: a => (a.feeCents > 0 ? money(a.feeCents) : '—') },
            { key: 'div', header: 'Division',
              cell: a => `${a.division}${a.hardshipCode ? ` · ${a.hardshipCode}` : ''}` },
            // Who recommended it, by name. An approver is taking responsibility for another
            // officer's judgement and is entitled to know whose it was.
            { key: 'recby', header: 'Recommended by',
              cell: a => (
                <>
                  {a.recommendedByName ?? a.recommendedBy}
                  <div className="muted xs">{dateOf(a.recommendedAt)}</div>
                </>
              ) },
            { key: 'waiting', header: 'Waiting for',
              cell: a => ROLE_LABELS[a.approverRole] ?? a.approverRole },
            { key: 'act', header: 'Decision', wide: true, bare: true,
              cell: a => (
                <>
                  {a.blocked ? (
                    <span className="tag stop" title={a.blockedReason ?? ''}>Blocked by rule</span>
                  ) : mayApprove(a) ? (
                    <div className="row-actions">
                      <button className="btn" onClick={() => decide(a, 'return')}>Send back</button>
                      <button className="btn pri" onClick={() => decide(a, 'approve')}>Approve</button>
                    </div>
                  ) : (
                    <span className="muted sm">Not yours to approve</span>
                  )}
                  {note[a.id] && <div className="muted xs note">{note[a.id]}</div>}
                </>
              ) },
          ]}
        />
      </section>
    </div>
  )
}
